import * as React from 'react';
import { Link } from 'react-router';
import styled, { css } from 'styled-components';
import { rem, rgba } from 'polished';
import {
  ClearfixStyles,
  VisuallyHiddenStyles,
} from '../../../theme/libraries/h5b-helpers';
import { connect } from 'react-redux';
import { capitalizeFirstLetter, getUrl, prop } from '../../../utilities';
import {
  setCategoryChanged,
  setCurrentCategoryId,
  setCurrentSubgroupVirtualId,
} from '../../../containers/App/actions';
import { ChematCustomerType } from '../../../library/ChematCustomerType';
import { Media } from '../../../theme/media';
import Banner from '../Banner/Banner';
import { __ } from '@bart.sk-eshop/react-i18n';

const Wrapper = styled.div``;

export const CategoryListWrapper = styled.ul`
  background: ${({ theme }) => theme.color.white};
  border-radius: ${rem(2)};
  font-weight: 700;
  margin: 0;
  padding: 0;
  ${({ theme }) => theme.media('sm')} {
    box-shadow: ${({ theme }) => theme.boxShadow.small};
  }
  margin-bottom: ${rem(10)};
  ${ClearfixStyles};
`;

export const Item = styled.li`
  list-style: none;
  &.hidden {
    display: none;
  }
  &.active {
    font-weight: 700;
    background: ${({ theme }) => theme.color.secondaryLight};
  }
  ${ClearfixStyles};
`;

const ItemRow = styled.div`
  width: 100%;
  ${ClearfixStyles};
  ul ul ul & {
    padding-left: ${rem(8)};
  }
  ul ul ul ul & {
    padding-left: ${rem(16)};
  }
  ul ul ul ul ul & {
    padding-left: ${rem(24)};
  }
`;

const ItemCheckboxWrap = styled.div`
  float: left;
  width: ${rem(36)};
  height: ${rem(36)};
  padding: ${rem(6)};
`;

const ItemCheckboxLabel = styled.label`
  cursor: pointer;
`;

const ItemCheckboxBox = styled.span`
  display: inline-block;
  background: ${({ theme }) => rgba(theme.color.white, 0.24)};
  border: ${rem(2)} solid ${({ theme }) => rgba(theme.color.black, 0.07)};
  border-radius: ${rem(1)};
  ${({ theme }) => theme.size(24)};
  padding: ${rem(10)};
  position: relative;
  pointer-events: none;
  &:before,
  &:after {
    display: none;
    content: '';
    background: ${({ theme }) => theme.color.gray80};
    ${({ theme }) => theme.size(14, 2)};
    position: absolute;
    top: ${rem(9)};
    left: ${rem(3)};
  }
  &:after {
    ${({ theme }) => theme.transition('transform')};
    transform: rotate(90deg);
  }
  ${({ hasChildren }) => {
    if (hasChildren) {
      return css`
        pointer-events: auto;
        &:before,
        &:after {
          display: block;
        }
      `;
    }
  }}
`;

const SubList = styled.ul`
  clear: both;
  font-weight: 400;
  width: 100%;
  margin: 0;
  padding: 0;
  & & {
    // display: none;
  }
  &:first-child {
    font-weight: 700;
  }
  li.active {
    background: ${({ theme }) => theme.color.secondaryLight};
    font-weight: 700;
    span {
      background: rgba(255, 255, 255, 1);
      border: ${rem(2)} solid rgba(243, 228, 137, 1);
      &:before,
      &:after {
        background: rgba(209, 193, 92, 1);
      }
    }
    &.alone,
    li.active {
      background: ${({ theme }) => theme.color.secondary};
      span {
        background: rgba(255, 247, 195, 1);
        border: ${rem(2)} solid rgba(225, 194, 0, 1);
        &:before,
        &:after {
          background: rgba(175, 152, 2, 1);
        }
      }
    }
  }
  ${({ opened }) => {
    if (opened) {
      return css`
        display: block;
      `;
    }
  }}
`;

const ItemCheckbox = styled.input`
  ${VisuallyHiddenStyles};
  &:checked + ${ItemCheckboxBox} {
    &:after {
      transform: rotate(0deg);
    }
  }
  &:checked ~ ${SubList} {
    display: block;
  }
`;

const ItemLinkStyles = css`
  display: block;
  float: left;
  color: inherit;
  font-size: ${rem(15)};
  text-decoration: none;
  width: 100%;
  padding: ${rem(7)} ${rem(8)} ${rem(7)} ${rem(40)};
  margin-left: ${rem(-36)};
  ${({ theme }) => theme.transition('background-color')};
`;

export const ItemLink = styled(Link)`
  ${ItemLinkStyles};
  &:hover {
    text-decoration: none;
  }
`;

const SubItem = styled.li`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
`;

interface CategoriesListNewProps {
  dispatch: any;
  categoryGroup: any[];
  className?: string;
  propsPath?: string;
  currentPath?: string;
  reloadState?: boolean;
  currentCategoryId?: number;
  currentCategoryGroupId?: number;
  currentCategoryGroup?: any;
  currentSubgroupVirtualId?: string | null;
  user?: any;
}

interface CategoriesListNewStates {
  checkedItems: {};
}

class CategoriesListNew extends React.PureComponent<
  CategoriesListNewProps,
  CategoriesListNewStates
> {
  // Instance variable — updated synchronously before dispatch, no async setState race
  private currentActiveSubgroupVirtualId: string | null = null;

  constructor(props) {
    super(props);
    this.currentActiveSubgroupVirtualId =
      this.deriveActiveSubgroupVirtualId(props);
    this.state = { checkedItems: this.getCheckedItems() };
    this.handleChange = this.handleChange.bind(this);
  }

  private deriveActiveSubgroupVirtualId(props = this.props): string | null {
    if (!props.categoryGroup || !props.currentCategoryId) return null;

    // Priority 1: use Redux-persisted subgroup virtual id as hint.
    // This survives component remounts (e.g. navigating from subgroup page to product detail).
    const reduxHint = props.currentSubgroupVirtualId;
    if (reduxHint) {
      const hintNode = props.categoryGroup.find(
        (item) => item.category_id === reduxHint,
      );
      if (
        hintNode?.children?.length &&
        this.treeContainsCategoryId(hintNode.children, props.currentCategoryId)
      ) {
        return reduxHint;
      }
    }

    const existing = this.currentActiveSubgroupVirtualId;
    if (existing) {
      const subgroupItem = props.categoryGroup.find(
        (item) => item.category_id === existing,
      );
      if (
        subgroupItem?.children?.length &&
        this.treeContainsCategoryId(
          subgroupItem.children,
          props.currentCategoryId,
        )
      ) {
        return existing;
      }
    } else {
      // existing is null (root) — verify category really is at root
      const existsInRoot = props.categoryGroup.some((item) => {
        if (
          typeof item.category_id === 'string' &&
          item.category_id.startsWith('subgroup_')
        ) {
          return false;
        }
        return this.treeContainsCategoryId([item], props.currentCategoryId);
      });
      if (existsInRoot) return null;
    }

    // Existing context is no longer valid — derive fresh
    // Prefer root if available
    const existsInRoot = props.categoryGroup.some((item) => {
      if (
        typeof item.category_id === 'string' &&
        item.category_id.startsWith('subgroup_')
      ) {
        return false;
      }
      return this.treeContainsCategoryId([item], props.currentCategoryId);
    });
    if (existsInRoot) return null;

    // Otherwise find the first subgroup containing it
    for (const item of props.categoryGroup) {
      const id = item.category_id;
      if (
        typeof id === 'string' &&
        id.startsWith('subgroup_') &&
        item.children?.length
      ) {
        if (
          this.treeContainsCategoryId(item.children, props.currentCategoryId)
        ) {
          return id as string;
        }
      }
    }
    return null;
  }

  private treeContainsCategoryId(categories: any[], categoryId: any): boolean {
    for (const cat of categories) {
      if (cat.category_id === categoryId) return true;
      if (
        cat.children?.length &&
        this.treeContainsCategoryId(cat.children, categoryId)
      )
        return true;
    }
    return false;
  }

  public getCheckedItems() {
    let checkedItems = {};
    let isNewCheckedItem = false;
    if (!this.props.categoryGroup) {
      return checkedItems;
    }
    for (const categoryGroup of this.props.categoryGroup) {
      if (!isNewCheckedItem) {
        [checkedItems, isNewCheckedItem] = this.getCurrentCategories(
          categoryGroup,
          checkedItems,
          null,
          this.currentActiveSubgroupVirtualId,
        );
        if (isNewCheckedItem) {
          checkedItems[categoryGroup.category_id] = true;
        }
      }
    }
    return checkedItems;
  }

  public getCurrentCategories = (
    category,
    checkedItems,
    insideSubgroupVirtualId: string | null = null,
    activeSubgroupVirtualId: string | null = null,
  ) => {
    let newCheckedItems = { ...checkedItems };
    let isNewCheckedItem = false;

    if (!category) {
      return [newCheckedItems, isNewCheckedItem];
    }

    const isSubgroupNode =
      typeof category.category_id === 'string' &&
      category.category_id.startsWith('subgroup_');
    const subgroupContextForChildren = isSubgroupNode
      ? (category.category_id as string)
      : insideSubgroupVirtualId;

    // Composite key: items inside a subgroup get "subgroup_5_123", root items get "123"
    const checkKey = insideSubgroupVirtualId
      ? `${insideSubgroupVirtualId}_${category.category_id}`
      : category.category_id;

    if (this.props.currentCategoryId === category.category_id) {
      // Only activate this occurrence if it matches the tracked subgroup context
      if (insideSubgroupVirtualId === activeSubgroupVirtualId) {
        newCheckedItems[checkKey] = true;
        isNewCheckedItem = true;
      }
    }

    // Match subgroup virtual items (category_id = "subgroup_<id>")
    if (
      !isNewCheckedItem &&
      this.props.currentCategoryGroupId &&
      category.category_id === `subgroup_${this.props.currentCategoryGroupId}`
    ) {
      newCheckedItems[category.category_id] = true;
      isNewCheckedItem = true;
    }

    if (category.children?.length && !isNewCheckedItem) {
      for (const children of category.children) {
        if (!isNewCheckedItem) {
          [newCheckedItems, isNewCheckedItem] = this.getCurrentCategories(
            children,
            newCheckedItems,
            subgroupContextForChildren,
            activeSubgroupVirtualId,
          );
          if (isNewCheckedItem) {
            newCheckedItems[checkKey] = true;
          }
        }
      }
    }

    return [newCheckedItems, isNewCheckedItem];
  };

  public componentDidUpdate(prevProps) {
    if (
      prevProps.currentPath !== this.props.currentPath ||
      prevProps.categoryGroup !== this.props.categoryGroup ||
      prevProps.currentCategoryId !== this.props.currentCategoryId ||
      prevProps.currentCategoryGroupId !== this.props.currentCategoryGroupId
    ) {
      if (prevProps.categoryGroup !== this.props.categoryGroup) {
        // categoryGroup changed (new page) — re-derive context from tree
        this.currentActiveSubgroupVirtualId =
          this.deriveActiveSubgroupVirtualId();
      }
      this.setState({ checkedItems: this.getCheckedItems() });
    }
  }

  public handleChange = (e) => {
    const item = e.target.name;
    const isChecked = e.target.checked;
    this.setState((prevState) => {
      const newCheckedItems = { ...prevState.checkedItems };
      newCheckedItems[item] = isChecked;
      return {
        ...prevState,
        checkedItems: newCheckedItems,
      };
    });
  };

  public onClickLink = (id, subgroupVirtualId: string | null = null) => {
    // Persist subgroup context to Redux so it survives component remounts
    // (e.g. navigating from subgroup category page to product detail page)
    this.props.dispatch(setCurrentSubgroupVirtualId(subgroupVirtualId));
    // Set synchronously BEFORE dispatch so componentDidUpdate reads the correct value
    this.currentActiveSubgroupVirtualId = subgroupVirtualId;
    this.props.dispatch(setCurrentCategoryId(id));
    this.props.dispatch(setCategoryChanged(true));
  };

  public render() {
    const { className, categoryGroup, user } = this.props;

    const getCategory = (item) => {
      if (
        (!user || user.skvelko_type === ChematCustomerType.RETAIL) &&
        item.category_number === 'KAT_SEASON'
      ) {
        return '';
      }
      if (
        user &&
        user.skvelko_type !== ChematCustomerType.RETAIL &&
        item.category_number === 'KAT_ACTION'
      ) {
        return '';
      }

      return (
        <Item
          key={item.category_id}
          className={this.state.checkedItems[item.category_id] ? 'active' : ''}
        >
          <SubList>{getSubcategory(item)}</SubList>
        </Item>
      );
    };

    const getSubcategory = (
      item,
      parentSubgroupVirtualId: string | null = null,
    ) => {
      const hasChildren = item.children && item.children.length;

      // Composite key — unique per position in tree
      const checkKey = parentSubgroupVirtualId
        ? `${parentSubgroupVirtualId}_${item.category_id}`
        : item.category_id;

      // If this is a subgroup virtual node, its children are "inside" it
      const isSubgroupNode =
        typeof item.category_id === 'string' &&
        item.category_id.startsWith('subgroup_');
      const subgroupContextForChildren = isSubgroupNode
        ? (item.category_id as string)
        : parentSubgroupVirtualId;

      let clazz = this.state.checkedItems[checkKey] ? 'active' : '';
      if (!hasChildren) {
        clazz += ' alone';
      }

      return (
        <SubItem key={checkKey} className={clazz}>
          <ItemRow>
            <ItemCheckboxWrap>
              <ItemCheckboxLabel>
                {hasChildren ? (
                  <ItemCheckbox
                    value="1"
                    type="checkbox"
                    name={checkKey}
                    checked={this.state.checkedItems[checkKey] === true}
                    onChange={this.handleChange}
                    aria-label={
                      __('Otvoriť kategóriu') +
                      ' ' +
                      capitalizeFirstLetter(item.category_name)
                    }
                  />
                ) : (
                  ''
                )}
                <ItemCheckboxBox hasChildren={hasChildren} />
              </ItemCheckboxLabel>
            </ItemCheckboxWrap>
            <ItemLink
              to={getUrl(item.url)}
              onClick={this.onClickLink.bind(
                null,
                item.category_id,
                parentSubgroupVirtualId,
              )}
            >
              {capitalizeFirstLetter(item.category_name)}
            </ItemLink>
          </ItemRow>
          {this.state.checkedItems[checkKey] && hasChildren ? (
            <SubList>
              {item.children.map((children) =>
                getSubcategory(children, subgroupContextForChildren),
              )}
            </SubList>
          ) : (
            ''
          )}
        </SubItem>
      );
    };

    return (
      <Wrapper>
        {this.props.currentCategoryGroup?.uniqid === 'FURNITURE_SCHOOL' ? (
          <Media greaterThanOrEqual="lg">
            <Banner url={'/cenova-ponuka'} background={'images/banner1.jpg'} />
          </Media>
        ) : (
          ''
        )}

        <CategoryListWrapper className={className}>
          {categoryGroup ? categoryGroup.map((item) => getCategory(item)) : ''}
        </CategoryListWrapper>

        {this.props.currentCategoryGroup?.uniqid === 'FURNITURE_SCHOOL' ? (
          <Media greaterThanOrEqual="lg">
            <Banner
              url={
                '/pre-skolske-zariadenia/referencie-a-realizacie/triedy-a-skd'
              }
              background={'images/banner4.webp'}
            />
            <Banner
              url={'/pre-skolske-zariadenia/doprava-do-skol-zdarma'}
              background={'images/banner2.jpg'}
            />
            <Banner
              url={'/pre-skolske-zariadenia/katalog-komplexne-vybavenie-skol'}
              background={'images/banner3.jpg'}
            />
          </Media>
        ) : (
          ''
        )}
      </Wrapper>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    currentPath: prop(state.routing, 'locationBeforeTransitions.pathname', ''),
    currentCategoryId: prop(state, 'general.currentCategoryId'),
    currentCategoryGroupId: prop(state, 'general.currentCategoryGroupId'),
    currentCategoryGroup: prop(state, 'general.currentCategoryGroup'),
    currentSubgroupVirtualId: prop(
      state,
      'general.currentSubgroupVirtualId',
      null,
    ),
  };
};

export default connect(mapStateToProps)(CategoriesListNew);
